import React, { useState } from 'react';

import calendarPrimarySvg from '@assets/img/icons/calendar-primary.svg';
import editOutlinedSvg from '@assets/img/icons/edit-outlined.svg';
import { WarehouseAddEditDialog } from '@components/admin/agency/WarehouseAddEditDialog';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { getCityLabelByName } from '@data/cities';
import { warehouseStrings } from '@data/warehouse';
import { usePutWarehouse } from '@hooks/mutation/dashboard/usePutWarehouse';
import { Warehouse } from '@models/Warehouse';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { url } from '@utils/pages';
import { stringifySearch } from '@utils/types';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, common) => ({
  gridContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  warehouseCard: {
    padding: '0.75rem',
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  warehouseInfoContainer: {
    display: 'grid',
    gridTemplateColumns: 'min-content 1fr',
    rowGap: '5px',
  },
  key: {
    display: 'inline-block',
    minWidth: '6rem',
    ...common.textGray500,
  },
  editLink: {
    ...common.link,
    whiteSpace: 'nowrap',
  },
}));

interface WarehouseCardProps {
  warehouse: Warehouse;
  isAdmin?: boolean;
}

export const WarehouseCard: React.FC<WarehouseCardProps> = (props) => {
  const { warehouse, isAdmin } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const putWarehouse = usePutWarehouse(warehouse.id);
  const history = useHistory();
  const currentPath = window.location.pathname + window.location.search;

  return (
    <>
      <SpinnerModal open={putWarehouse.isLoading} />
      <WarehouseAddEditDialog
        open={isEditDialogOpen}
        initialValues={warehouse}
        action="edit"
        onClose={() => setIsEditDialogOpen(false)}
        onSubmit={async (values) => {
          await putWarehouse.mutateAsync(values);
          setIsEditDialogOpen(false);
        }}
      />
      <div className={classes.warehouseCard}>
        <div className={clsx(classes.flexInline, classes.justifyBetween, classes.itemsStart)}>
          <span>
            <span className={classes.fontWeight700}>{warehouse.name}</span>-{warehouse.name_en}
          </span>
          <div className={classes.flexInline}>
            {isAdmin && (
              <img
                src={editOutlinedSvg}
                alt="Calendar Primary"
                className={classes.baseButton}
                style={{ marginLeft: 20 }}
                onClick={() => setIsEditDialogOpen(true)}
              />
            )}
            <HorizontalSpace width={20} />
            <img
              src={calendarPrimarySvg}
              alt="Calendar Primary"
              onClick={() =>
                history.push(
                  isAdmin
                    ? `${url('/admin/widgets/WarehouseCalendar')}?${stringifySearch({
                        warehouseId: warehouse.id,
                        agencyId: warehouse.agency_id,
                        from: currentPath,
                      })}`
                    : `${url('/admin/agency/WarehouseCalendar')}?${stringifySearch({
                        warehouseId: warehouse.id,
                        from: currentPath,
                      })}`,
                )
              }
              className={classes.baseButton}
              style={{ marginRight: 18 }}
            />
          </div>
        </div>
        <VerticalSpace height={10} />
        <div className={classes.warehouseInfoContainer}>
          <span className={classes.key}>{warehouseStrings.city}</span>
          {getCityLabelByName(warehouse.city)}
          <span className={classes.key}>{warehouseStrings.zipcode}</span>
          {warehouse.zipcode || '-'}
          <span className={classes.key}>{warehouseStrings.address1}</span>
          {warehouse.address1 || '-'}
          <span className={classes.key}>{warehouseStrings.address2}</span>
          {warehouse.address2 || '-'}
          <span className={classes.key}>{warehouseStrings.principalName}</span>
          {warehouse.principal_name || '-'}
          <span className={classes.key}>{warehouseStrings.phone}</span>
          {warehouse.phone || '-'}
          <span className={classes.key}>{warehouseStrings.memo}</span>
          {warehouse.memo || '-'}
        </div>
      </div>
    </>
  );
};

interface WarehouseGridProps {
  warehouses: Warehouse[];
  isAdmin?: boolean;
}

export const WarehouseGrid: React.FC<WarehouseGridProps> = (props) => {
  const { warehouses, isAdmin } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  return (
    <div className={classes.gridContainer}>
      {warehouses.map((warehouse) => (
        <WarehouseCard isAdmin={isAdmin} key={warehouse.id} warehouse={warehouse} />
      ))}
    </div>
  );
};
